import type { QueryKey, UseQueryOptions } from '@tanstack/react-query';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query/src/types';
import type { AxiosError } from 'axios';
import { useEffect } from 'react';
import { useConnection } from '@gov-nx/core/hooks';
import type { ResponseError } from '../errors/errors.types';
import { usePoResponseError } from '../errors/usePoResponseError';
import { logger } from '../logger';
import type { PoQueryConfig } from './types';

export const usePoQuery = <
	TQueryFnData = unknown,
	TData = TQueryFnData,
	TQueryKey extends QueryKey = QueryKey,
>(
	options: UseQueryOptions<
		TQueryFnData,
		AxiosError<ResponseError>,
		TData,
		TQueryKey
	>,
	config?: PoQueryConfig
): UseQueryResult<TData, AxiosError<ResponseError>> => {
	const { handleError } = usePoResponseError({
		doNotShowToastError: config?.doNotShowToastError,
	});
	const queryClient = useQueryClient();
	const { isOffline } = useConnection();

	useEffect(() => {
		const cached =
			options.queryKey && queryClient.getQueryData<TData>(options.queryKey);
		if (cached && options.onSuccess) {
			options.onSuccess(cached);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
	}, []);

	return useQuery({
		...options,
		onError: (error) => {
			// Note: Don't throw errors when no internet connection
			if (isOffline) return;

			if (config?.errorIgnoreFilter && config.errorIgnoreFilter(error)) {
				return;
			}

			logger('usePoQuery options.queryFn', [
				options.queryKey ? options.queryKey.toString() : '',
				error.message,
				error.name,
			]);

			handleError(error);

			options.onError && options.onError(error);
		},
	});
};
